import { useEffect, useState, useCallback } from "react"
import { useGoogleAds, useMetaAds } from "hooks"
import { useCanCreateCampaign } from "hooks/useCanCreateCampaign"
import { adminApi, googleApi, metaApi } from "api"
import { Edit2, Check, X, Play, Pause } from "lucide-react"
import { useSearchParams, useNavigate } from "react-router-dom"
import toast from "react-hot-toast"
import NoAdsConnectedSection from "components/common/NoAdsConnectedSection"
import MobileTopNavBar from 'components/MobileTopNavBar'
import CampaignsFilterBar from 'components/CampaignsFilterBar'
import MobileTable from 'components/ui/MobileTable'
import CampaignMobileCard from 'components/campaigns/CampaignMobileCard'
import { useAdsModal } from 'components/context/ModalsContext';

const CampaignsPage = ({ isAdmin = false }) => {
  const [, setUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)
  const navigate = useNavigate()
  const { canCreateCampaign } = useCanCreateCampaign()
  const { openUpdateBusinessDataModal } = useAdsModal()
  const [searchParams, setSearchParams] = useSearchParams()
  const platformParam = searchParams.get("platform")
  const statusParam = searchParams.get("status")
  const validPlatforms = ["google", "meta", "all"]
  const validStatuses = ["ENABLED", "ACTIVE", "PAUSED", "REMOVED", "DELETED", "all"]

  const isValidPlatform = validPlatforms.includes(platformParam)
  const isValidStatus = validStatuses.includes(statusParam)

  const initialPlatform = isValidPlatform ? platformParam : "all"
  const initialStatus = isValidStatus ? statusParam : "ACTIVE"

  const [activePlatform, setActivePlatform] = useState(initialPlatform)
  const [activeStatus, setActiveStatus] = useState(initialStatus)
  const [_loadedTabs, setLoadedTabs] = useState({ google: false, meta: false })
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [campaignSearch, setCampaignSearch] = useState("")
  const [showSearch, setShowSearch] = useState(false)
  
  // Mantener el campo de búsqueda visible si hay texto
  useEffect(() => {
    if (campaignSearch && !showSearch) {
      setShowSearch(true)
    }
  }, [campaignSearch, showSearch])
  
  // Estados para edición inline
  const [editingCampaign, setEditingCampaign] = useState(null)
  const [editValues, setEditValues] = useState({})

  useEffect(() => {
    if (!isValidPlatform || !isValidStatus) {
      setSearchParams({
        platform: initialPlatform,
        status: initialStatus,
      })
    }
  }, [initialPlatform, initialStatus, isValidPlatform, isValidStatus, setSearchParams])

  useEffect(() => {
    setSearchParams({
      platform: activePlatform,
      status: activeStatus,
    });
  }, [activePlatform, activeStatus, setSearchParams]);

  const {
    isConnected: isGoogleConnected,
    fetchCampaigns: fetchGoogleCampaigns,
    fetchCampaignsForUser: fetchGoogleCampaignsForUser,
    campaigns: googleCampaigns = [],
    adminCampaigns: googleAdminCampaigns = [],
    loadingCampaigns: loadingGoogleCampaigns,
    activeAccount: activeGoogleAccount,
    fetchActiveAccount: fetchGoogleActiveAccount,
    connectGoogleAds,
  } = useGoogleAds()

  const {
    isConnected: isMetaConnected,
    fetchCampaigns: fetchMetaCampaigns,
    fetchCampaignsForUser: fetchMetaCampaignsForUser,
    campaigns: metaCampaigns = [],
    adminCampaigns: metaAdminCampaigns = [],
    loadingCampaigns: loadingMetaCampaigns,
    activeAccount: activeMetaAccount,
    fetchActiveAccount: fetchMetaActiveAccount,
    startMetaOAuth,
  } = useMetaAds()

  useEffect(() => {
    const fetchUsers = async () => {
      if (isAdmin) {
        try {
          const response = await adminApi.getAllUsers()
          const list = response?.data || []
          setUsers(list)
          setSelectedUserId((prev) => prev ?? list?.[0]?.id ?? null)
        } catch (e) {
          console.error("Error fetching users", e)
        }
      } else {
        setUsers([])
        setSelectedUserId(null)
        setLoadedTabs({ google: false, meta: false })
      }
    }
    fetchUsers()
  }, [isAdmin])

  useEffect(() => {
    if (isAdmin && selectedUserId) {
      setLoadedTabs({ google: false, meta: false })
    }
  }, [isAdmin, selectedUserId])

  useEffect(() => {
    if (isGoogleConnected) {
      fetchGoogleActiveAccount()
    }
    if (isMetaConnected) {
      fetchMetaActiveAccount()
    }
  }, [isGoogleConnected, isMetaConnected, fetchGoogleActiveAccount, fetchMetaActiveAccount])

  const loadAllNeededData = useCallback(async ({ force = false } = {}) => {
    setIsRefreshing(true)

    const loadGoogle = async () => {
      try {
        if (activePlatform === "all" || activePlatform === "google") {
          if (isAdmin && selectedUserId) {
            await fetchGoogleCampaignsForUser(selectedUserId, { force })
          } else if (isGoogleConnected) {
            await fetchGoogleCampaigns({ force })
          }
          setLoadedTabs((prev) => ({ ...prev, google: true }))
        }
      } catch (error) {
        console.error("Error loading Google campaigns:", error)
        toast.error(`Error al cargar campañas de Google: ${error.message || "Error al cargar campañas"}`)
      }
    }

    const loadMeta = async () => {
      try {
        if (activePlatform === "all" || activePlatform === "meta") {
          if (isAdmin && selectedUserId) {
            await fetchMetaCampaignsForUser(selectedUserId, { force })
          } else if (isMetaConnected) {
            await fetchMetaCampaigns({ force })
          }
          setLoadedTabs((prev) => ({ ...prev, meta: true }))
        }
      } catch (error) {
        console.error("Error loading Meta campaigns:", error)
        toast.error(error?.message || "Error al cargar campañas")
      }
    }

    await Promise.allSettled([loadGoogle(), loadMeta()])

    setIsRefreshing(false)
  }, [activePlatform, selectedUserId, isAdmin, isGoogleConnected, isMetaConnected, fetchGoogleCampaignsForUser, fetchGoogleCampaigns, fetchMetaCampaignsForUser, fetchMetaCampaigns])

  useEffect(() => {
    loadAllNeededData();
  }, [activePlatform, selectedUserId, loadAllNeededData])

  const formatGoogle = (list) =>
    list.map((c) => ({
      id: c.id,
      name: c.name,
      status: c.status === "ENABLED" ? "ACTIVE" : c.status,
      platform: "google",
      isEsporaiCampaign: c.is_esporai_campaign,
      serving_status: c.serving_status,
      budget: c.budget_amount_micros ? c.budget_amount_micros / 1000000 : 0,
      budgetType: c.budget_period === 'CUSTOM_PERIOD' ? 'campaign_total' : c.budget_period === 'DAILY' ? 'daily' : 'campaign_total',
      start_date: c.start_date,
      end_date: c.end_date,
    }))

  const formatMeta = (list) =>
    list.map((c) => ({
      id: c.id,
      name: c.name,
      status: c.status,
      platform: "meta",
      isEsporaiCampaign: c.is_esporai_campaign,
      budget: c.daily_budget 
        ? parseFloat(c.daily_budget) / 100 
        : c.lifetime_budget 
          ? parseFloat(c.lifetime_budget) / 100 / 30
          : null,
      hasBudgetAtCampaignLevel: !!(c.daily_budget || c.lifetime_budget),
      budgetType: c.daily_budget ? 'daily' : c.lifetime_budget ? 'lifetime' : null,
      start_date: c.start_time,
      end_date: c.stop_time,
    }))

  const getFilteredCampaigns = () => {
    let campaigns = []

    if (activePlatform === "google" || activePlatform === "all") {
      const googleData = isAdmin ? googleAdminCampaigns : googleCampaigns
      campaigns = [...campaigns, ...formatGoogle(googleData)]
    }

    if (activePlatform === "meta" || activePlatform === "all") {
      const metaData = isAdmin ? metaAdminCampaigns : metaCampaigns
      campaigns = [...campaigns, ...formatMeta(metaData)]
    }

    if (activeStatus !== "all") {
      const statusFilter = activeStatus === "ACTIVE" ? ["ACTIVE", "ENABLED"] : [activeStatus];
      campaigns = campaigns.filter((campaign) => statusFilter.includes(campaign.status));
    }

    if (activeStatus === "ACTIVE") {
      campaigns = campaigns.filter((campaign) => {
        return !campaign?.serving_status || campaign.serving_status === "SERVING";
      })
    }

    if (campaignSearch.trim()) {
      const term = campaignSearch.trim().toLowerCase()
      campaigns = campaigns.filter(
        (c) =>
          c.name?.toLowerCase().includes(term) ||
          c.id?.toString().toLowerCase().includes(term)
      )
    }    

    return campaigns
  }

  const formattedCampaigns = getFilteredCampaigns()

  const handleRefresh = () => {
    setLoadedTabs({ google: false, meta: false })
    loadAllNeededData({ force: true })
  }

  const isLoading = loadingGoogleCampaigns || loadingMetaCampaigns || isRefreshing

  const hasAnyConnection = isGoogleConnected || isMetaConnected
  const hasConnectionForActivePlatform =
    activePlatform === 'google'
      ? isGoogleConnected
      : activePlatform === 'meta'
        ? isMetaConnected
        : hasAnyConnection

  // Sin cuentas conectadas: mostrar título, descripción y NoAdsConnectedSection (sin filtros ni botones)
  if (!hasAnyConnection && !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 p-6">
        <MobileTopNavBar hideBack={false} sticky={true} title="Campañas" transparent={true} hideBrandSelector={true} />
        <div className="max-w-7xl mx-auto">
          <NoAdsConnectedSection
            connectGoogleAds={connectGoogleAds}
            startMetaOAuth={startMetaOAuth}
            activePlatform={'all'}
            activeGoogleAccount={null}
            activeMetaAccount={null}
            context={'campaigns'}
          />
        </div>
      </div>
    )
  }

  // Funciones de edición
  const handleStartEdit = (campaign) => {
    // Para Meta sin presupuesto a nivel campaña, no permitir editar presupuesto
    if (campaign.platform === 'meta' && !campaign.hasBudgetAtCampaignLevel) {
      setEditingCampaign(campaign.id)
      setEditValues({
        name: campaign.name,
        budget: 0, // No se editará
        start_date: campaign.start_date,
        end_date: campaign.end_date,
      })
    } else {
      setEditingCampaign(campaign.id)
      setEditValues({
        name: campaign.name,
        budget: campaign.budget,
        start_date: campaign.start_date,
        end_date: campaign.end_date,
      })
    }
  }

  const handleCancelEdit = () => {
    setEditingCampaign(null)
    setEditValues({})
  }

  const handleSaveEdit = async (campaign) => {
    try {
      setIsRefreshing(true);
      
      const payload = {
        name: editValues.name,
        start_date: editValues.start_date,
        end_date: editValues.end_date,
      };
      
      // Agregar presupuesto solo si la campaña lo tiene a nivel campaña
      if (campaign.platform === 'meta' && campaign.hasBudgetAtCampaignLevel) {
        payload.daily_budget = Math.round(editValues.budget * 100); // Convertir a centavos
      } else if (campaign.platform === 'google') {
        payload.budget_amount_micros = Math.round(editValues.budget * 1000000); // Convertir a micros
      }
      
      if (campaign.platform === 'google') {
        await googleApi.updateGoogleAdsCampaign(campaign.id, {
          payload,
          isMultipart: false
        });
      } else if (campaign.platform === 'meta') {
        await metaApi.updateMetaAdsCampaign(campaign.id, {
          payload,
          isMultipart: false
        });
      }
      
      toast.success('Campaña actualizada correctamente');
      setEditingCampaign(null);
      setEditValues({});
      
      // Refrescar datos
      await loadAllNeededData({ force: true });
    } catch (error) {
      console.error('Error updating campaign:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al actualizar';
      toast.error(`Error al actualizar la campaña: ${errorMessage}`);
    } finally {
      setIsRefreshing(false);
    }
  }

  const handleToggleCampaignStatus = async (campaign) => {
    try {
      const isPaused = campaign.status === 'PAUSED';
      const newStatus = isPaused ? 'ACTIVE' : 'PAUSED';
      const googleStatus = isPaused ? 'ENABLED' : 'PAUSED';
      
      setIsRefreshing(true);
      
      if (campaign.platform === 'google') {
        await googleApi.updateGoogleAdsCampaign(campaign.id, {
          payload: { status: googleStatus },
          isMultipart: false
        });
      } else if (campaign.platform === 'meta') {
        await metaApi.updateMetaAdsCampaign(campaign.id, {
          payload: { status: newStatus },
          isMultipart: false
        });
      }
      
      toast.success(`Campaña ${isPaused ? 'activada' : 'pausada'} correctamente`);
      
      // Refrescar la lista de campañas
      await loadAllNeededData({ force: true });
      
    } catch (error) {
      console.error('Error toggling campaign status:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Error al cambiar el estado';
      toast.error(`Error: ${errorMessage}`);
    } finally {
      setIsRefreshing(false);
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('es-ES')
  }

  // Manejador para hacer click en una fila
  const handleRowClick = (campaign) => {
    // No navegar si está en modo edición
    if (editingCampaign === campaign.id) {
      return;
    }
    navigate(`/campaigns/${campaign.platform}/${campaign.id}`);
  }

  // Mobile card renderer
  const campaignMobileCardRenderer = (campaign) => {
    return (
      <CampaignMobileCard
        campaign={campaign}
        isEditing={editingCampaign === campaign.id}
        editValues={editValues}
        onStartEdit={() => handleStartEdit(campaign)}
        onSaveEdit={() => handleSaveEdit(campaign)}
        onCancelEdit={handleCancelEdit}
        onToggleStatus={() => handleToggleCampaignStatus(campaign)}
        onClick={() => handleRowClick(campaign)}
        onEditValuesChange={(newValues) => setEditValues(newValues)}
        currency={campaign.platform === 'meta' ? activeMetaAccount?.currency : activeGoogleAccount?.currency}
        disabled={isRefreshing}
      />
    )
  }

  // Definir columnas para la tabla de campañas
  const campaignsColumns = [
    {
      key: 'name',
      label: 'Nombre de la campaña',
      field: 'name',
      render: (value, campaign) => {
        const isEditing = editingCampaign === campaign.id
        
        return (
          <div className="flex items-center gap-2">
            {campaign.isEsporaiCampaign && (
              <div className="w-5 h-5 rounded-full bg-gradient-to-r from-fuchsia-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                <img src="/isologo.blanco.png" alt="Esporai" className="w-3 h-3" />
              </div>
            )}
            <div className="flex items-center gap-2 flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editValues.name}
                  onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                  className="bg-white/10 border border-white/30 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-fuchsia-400"
                />
              ) : (
                <span className="font-semibold text-white whitespace-normal break-words">{value}</span>
              )}
            </div>
          </div>
        )
      },
    },
    {
      key: 'platform',
      label: 'Plataforma',
      field: 'platform',
      render: (value) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border backdrop-blur-sm whitespace-nowrap ${
          value === 'google'
            ? 'border-blue-400/50 text-blue-400 bg-blue-500/10' 
            : 'border-purple-400/50 text-purple-400 bg-purple-500/10'
        }`}>
          {value === 'google' ? 'Google Ads' : 'Meta Ads'}
        </span>
      ),
    },
    {
      key: 'budget',
      label: 'Presupuesto',
      field: 'budget',
      render: (value, campaign) => {
        const isEditing = editingCampaign === campaign.id
        
        // Obtener la moneda de la cuenta activa según la plataforma
        const accountCurrency = campaign.platform === 'meta' 
          ? activeMetaAccount?.currency 
          : activeGoogleAccount?.currency;
        const displayCurrency = accountCurrency || 'USD';
        
        // Para Meta Ads sin presupuesto a nivel campaña
        if (campaign.platform === 'meta' && !campaign.hasBudgetAtCampaignLevel) {
          return (
            <span className="text-white/70 text-xs italic">
              A nivel ad set
            </span>
          )
        }
        
        return isEditing ? (
          <input
            type="number"
            step="0.01"
            value={editValues.budget}
            onChange={(e) => setEditValues({ ...editValues, budget: parseFloat(e.target.value) })}
            className="bg-white/10 border border-white/30 rounded px-2 py-1 text-white text-sm w-24 focus:outline-none focus:border-fuchsia-400"
          />
        ) : (
          <span className="font-semibold text-white">
            {displayCurrency} {value?.toFixed(2) || '0.00'}
          </span>
        )
      },
    },
    {
      key: 'budgetType',
      label: 'Tipo de presupuesto',
      field: 'budgetType',
      render: (value, campaign) => {
        // Para Meta Ads sin presupuesto a nivel campaña
        if (campaign.platform === 'meta' && !campaign.hasBudgetAtCampaignLevel) {
          return (
            <span className="text-white/70 text-xs">
              -
            </span>
          )
        }
        
        let label = '';
        let colorClass = '';
        
        if (value === 'daily') {
          label = 'Por día';
          colorClass = 'bg-blue-500/20 text-blue-300 border-blue-400/30';
        } else if (value === 'lifetime') {
          label = 'Total campaña';
          colorClass = 'bg-purple-500/20 text-purple-300 border-purple-400/30';
        } else if (value === 'campaign_total') {
          label = 'Total campaña';
          colorClass = 'bg-purple-500/20 text-purple-300 border-purple-400/30';
        } else {
          label = 'No especificado';
          colorClass = 'bg-white/10 text-white/50 border-white/20';
        }
        
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colorClass}`}>
            {label}
          </span>
        )
      },
    },
    {
      key: 'start_date',
      label: 'Fecha de Inicio',
      field: 'start_date',
      render: (value, campaign) => {
        const isEditing = editingCampaign === campaign.id
        
        return isEditing ? (
          <input
            type="date"
            value={editValues.start_date || ''}
            onChange={(e) => setEditValues({ ...editValues, start_date: e.target.value })}
            className="bg-white/10 border border-white/30 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-fuchsia-400"
          />
        ) : (
          <span className="text-white/90 text-sm">{formatDate(value)}</span>
        )
      },
    },
    {
      key: 'end_date',
      label: 'Fecha de Finalización',
      field: 'end_date',
      render: (value, campaign) => {
        const isEditing = editingCampaign === campaign.id
        
        return isEditing ? (
          <input
            type="date"
            value={editValues.end_date || ''}
            onChange={(e) => setEditValues({ ...editValues, end_date: e.target.value })}
            className="bg-white/10 border border-white/30 rounded px-2 py-1 text-white text-sm focus:outline-none focus:border-fuchsia-400"
          />
        ) : (
          <span className="text-white/90 text-sm">{formatDate(value)}</span>
        )
      },
    },
    {
      key: 'actions',
      label: 'Acciones',
      field: 'id',
      render: (campaignId, campaign) => {
        const isEditing = editingCampaign === campaignId
        const isPaused = campaign.status === 'PAUSED';
        
        return (
          <div 
            className="flex items-center gap-2 justify-center"
            onClick={(e) => e.stopPropagation()} // Evitar que el click en los botones active el onRowClick
          >
            {isEditing ? (
              <>
                {/* Botones Guardar/Cancelar cuando está editando */}
                <button
                  onClick={() => handleSaveEdit(campaign)}
                  disabled={isRefreshing}
                  className={`p-2 rounded-lg bg-green-500/20 border border-green-400/30 text-green-300 hover:bg-green-500/30 hover:border-green-400/50 transition-all duration-300 ${
                    isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Guardar cambios"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isRefreshing}
                  className="p-2 rounded-lg bg-red-500/20 border border-red-400/30 text-red-300 hover:bg-red-500/30 hover:border-red-400/50 transition-all duration-300"
                  title="Cancelar"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                {/* Botón Play/Pause */}
                <button
                  onClick={() => handleToggleCampaignStatus(campaign)}
                  disabled={isRefreshing}
                  className={`p-2 rounded-lg border transition-all duration-300 ${
                    isPaused
                      ? 'bg-green-500/20 border-green-400/30 text-green-300 hover:bg-green-500/30 hover:border-green-400/50'
                      : 'bg-yellow-500/20 border-yellow-400/30 text-yellow-300 hover:bg-yellow-500/30 hover:border-yellow-400/50'
                  } ${isRefreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={isPaused ? 'Activar campaña' : 'Pausar campaña'}
                >
                  {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                </button>
                
                {/* Botón Editar */}
                <button
                  onClick={() => handleStartEdit(campaign)}
                  disabled={isRefreshing}
                  className={`p-2 rounded-lg bg-blue-500/20 border border-blue-400/30 text-blue-300 hover:bg-blue-500/30 hover:border-blue-400/50 transition-all duration-300 ${
                    isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  title="Editar"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        )
      },
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 pt-20 px-6 pb-6">
      <MobileTopNavBar hideBack={false} sticky={true} title="Campañas" transparent={true} hideBrandSelector={true} />
      <CampaignsFilterBar
        activePlatform={activePlatform}
        setActivePlatform={setActivePlatform}
        activeStatus={activeStatus}
        setActiveStatus={setActiveStatus}
        campaignSearch={campaignSearch}
        setCampaignSearch={setCampaignSearch}
        showSearch={showSearch}
        setShowSearch={setShowSearch}
        onCreate={() => (canCreateCampaign ? navigate('/campaign-wizard') : openUpdateBusinessDataModal())}
        onRefresh={handleRefresh}
        isLoading={isLoading}
      />
      {/* Tabla de Campañas - borde más sutil para no duplicar el marco del filtro */}
      <div className="w-full px-2 sm:px-4 mb-8">
        <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/10 rounded-2xl p-4 sm:p-6 shadow-lg">
          <div className="relative z-10">
            {formattedCampaigns.length === 0 && !isLoading ? (
              !isAdmin && (
                !hasConnectionForActivePlatform
              ) ? (
                <NoAdsConnectedSection
                  connectGoogleAds={connectGoogleAds}
                  startMetaOAuth={startMetaOAuth}
                  activePlatform={activePlatform}
                  activeGoogleAccount={activeGoogleAccount}
                  activeMetaAccount={activeMetaAccount}
                />
              ) : (
                <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-xl p-10 text-center text-white/70">
                  No hay campañas.
                </div>
              )
            ) : (
              <MobileTable
                data={formattedCampaigns}
                columns={campaignsColumns}
                loading={isLoading}
                emptyMessage="No se encontraron campañas con los filtros seleccionados."
                onRowClick={handleRowClick}
                mobileCardRenderer={campaignMobileCardRenderer}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}


export default CampaignsPage
